import React, { useEffect, useMemo, useState, lazy, Suspense } from 'react';
import {
  StyledEngineProvider,
  ThemeProvider,
  createTheme,
  useTheme,
} from '@mui/material/styles';
// import { registerLicense } from '@syncfusion/ej2-base';
import { BrowserRouter as Router, useHistory } from 'react-router-dom';
import { Provider, connect } from 'react-redux';
import { store, persistor } from './redux/store';
import '@fontsource/ibm-plex-sans';
import './library/scss/index.scss';
import { ToastContainer } from 'react-toastify';
import './styles/toastStyles.css';
import 'react-toastify/dist/ReactToastify.css';
import { PersistGate } from 'redux-persist/integration/react';
import { history } from '../src/components/Helpers';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import { LicenseInfo } from '@mui/x-license';
import { removeLock } from './redux/actions';
import { getProfile } from './redux/actions';
import CircularProgress from '@mui/material/CircularProgress';
import { colors, applyFocusColor, initFocusColorFromStorage } from './components/Design_System/Tokens'
import { ToastBridge } from './components/ToastManager';

// Re-applies whatever focus/highlight color is already in localStorage (from a
// previous session) as soon as this module loads, so the CSS custom property
// backing colors.border.focus is populated before first paint rather than
// waiting on the profile-data fetch below.
initFocusColorFromStorage();

const RoutesApp = lazy(() => import('./components/Routing/Routes'));
const IdleTimerContainer = lazy(
  () => import('../src/components/Helpers/IdleTimerContainer'),
);
LicenseInfo.setLicenseKey(
  '7a15b5813c99260c238986b6120bba48Tz0xMjIyMDMsRT0xNzk1MzkxOTk5MDAwLFM9cHJlbWl1bSxMTT1zdWJzY3JpcHRpb24sUFY9aW5pdGlhbCxLVj0y',
);
//registerLicense("Ngo9BigBOggjHTQxAR8/V1NHaF1cWWhIfEx1RHxQdld5ZFRHallYTnNWUj0eQnxTdEZiW39ZcHVXT2NVUUV1Ww==")
// registerLicense(
//   'Ngo9BigBOggjHTQxAR8/V1NBaF5cXmZCf1FpRmJGdld5fUVHYVZUTXxaS00DNHVRdkdnWXxccnRSR2FZUEFyV0M=',
// );

const App = (props) => {
  const domHistory = useHistory();
  const [primary, setPrimary] = useState('#32009A');
  const [primaryDark, setPrimaryDark] = useState('#32009A');//#1976d2
  const [h3, setH3] = useState('1.1em');
  const [loading, setLoading] = useState(true); // Add loading state
  const [tabCount, setTabCount] = useState(0);
  const [promoActive, setPromoActive] = useState(false);
  const [isProfileLoading, setIsProfileLoading] = useState(false);
  const white = '#FFFFFF';
  const black = '#000000';
  // Only load profile after successful login
  useEffect(() => {
    // Assume 'loggedIn' flag in localStorage/sessionStorage is set on successful login
    if (localStorage.getItem('loggedIn') === 'true' || sessionStorage.getItem('loggedIn') === 'true') {
      setIsProfileLoading(true);
      props.getProfile();
    }
  }, []);

  // Add global ResizeObserver error handler to suppress non-critical errors
  useEffect(() => {
    const originalError = console.error;
    console.error = (...args) => {
      // Suppress ResizeObserver loop errors as they are non-critical
      if (args[0] && typeof args[0] === 'string' && args[0].includes('ResizeObserver loop completed with undelivered notifications')) {
        return;
      }
      // Call the original console.error for all other errors
      originalError.apply(console, args);
    };

    // Cleanup function to restore original console.error
    return () => {
      console.error = originalError;
    };
  }, []);

  // Helper function to apply the global font
  const applyGlobalFont = () => {
    const app = sessionStorage.getItem('application');
    let globalFont = props.profileData?.global_font;
    let fontImport = '';
    let fontFamilyCSS = '';

    // Default fallback
    if (!globalFont) {
      globalFont = "IBM Plex Sans, sans-serif";
    }

    // Handle special cases for known fonts (add more as needed)
    if (globalFont.includes('IBM Plex Sans')) {
      fontImport = `@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:ital,wght@0,100..700;1,100..700&display=swap');`;
    } else if (globalFont.includes('IBM Plex Serif')) {
      fontImport = `@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Serif:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;1,100;1,200;1,300;1,400;1,500;1,600;1,700&display=swap');`;
    } else if (globalFont.includes('Inter')) {
      fontImport = `@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600&display=swap');`;
    } else {
      // Generic Google Fonts loader for custom fonts (assume first part is font name)
      const fontName = globalFont.split(',')[0].replace(/['"]/g, '').trim().replace(/ /g, '+');
      fontImport = `@import url('https://fonts.googleapis.com/css?family=${fontName}&display=swap');`;
    }

    fontFamilyCSS = `* { font-family: ${globalFont} !important; }`;

    // Only create one style element for global font
    let fontStyle = document.getElementById('global-font-style');
    if (!fontStyle) {
      fontStyle = document.createElement('style');
      fontStyle.id = 'global-font-style';
      document.head.appendChild(fontStyle);
    }
    fontStyle.innerHTML = `${fontImport}\n${fontFamilyCSS}`;

    // Set a CSS variable for use in other styles if needed
    document.documentElement.style.setProperty('--selected-font', globalFont, 'important');

    // Handle theme class
    if (app === 'Promotion Planning') {
      document.body.classList.add('promotions-theme');
    } else {
      document.body.classList.remove('promotions-theme');
    }
  };

  useEffect(() => {
    applyGlobalFont();
  }, [props.profileData?.global_font]);

  useEffect(() => {
    const handleGlobalFontChange = () => {
      applyGlobalFont();
    };
    window.addEventListener('globalfontchange', handleGlobalFontChange);
    return () => {
      window.removeEventListener('globalfontchange', handleGlobalFontChange);
    };
  }, []);


  useEffect(() => {
    if (props.profileData) {
      localStorage.setItem('collaboration_id', props.profileData.collaboration_id);
      const applications = [
        'Customer Rebate',
        'Supplier Rebate',
        'Manufacturer Chargeback',
        'Sales Commission',
        'Price Management',
        'Profit Optimization',
        'Distributor Chargeback',
        'Royalty',
      ];
      if (
        props.profileData.app_types?.includes('Promotion Planning') &&
        !applications.some((value) =>
          props.profileData.app_types?.includes(value),
        )
      ) {
        setPrimaryDark('#FFC400');
        localStorage.setItem('navclr', '#FFC400');
        localStorage.setItem('color_theme', '#FFC400');
        applyFocusColor('#FFC400');
        setPromoActive(true);
      } else {
        setPromoActive(false);
        if (props.profileData && props.profileData.color_theme) {
          localStorage.setItem('color_theme', props.profileData.color_theme);
          setPrimaryDark(props.profileData.color_theme);
        }
        if (props.profileData && props.profileData.global_color) {
          localStorage.setItem('navclr', props.profileData.global_color);
          applyFocusColor(props.profileData.global_color);
          setPrimary(props.profileData.global_color);
        }
      }
      setIsProfileLoading(false);
    }
  }, [props.profileData]);

  // Font management utilities
  const loadGoogleFont = (fontFamily) => {
    try {
      // Remove any existing font style elements
      document.querySelectorAll('link[data-font-loaded="true"]').forEach(el => el.remove());

      const fontName = fontFamily.split(',')[0].replace(/['"]/g, '').trim();

      // Skip loading for system fonts
      if (fontName.match(/^(Arial|Helvetica|Times New Roman|Courier|Georgia|Verdana)$/i)) {
        return;
      }

      // Create and append the font link
      const link = document.createElement('link');
      link.href = `https://fonts.googleapis.com/css?family=${fontName.replace(/ /g, '+')}&display=swap`;
      link.rel = 'stylesheet';
      link.setAttribute('data-font-loaded', 'true');
      document.head.appendChild(link);

      // Force the browser to load the font
      document.fonts.load(`1em "${fontName}"`).catch(err => {
        console.error('Error loading font:', err);
      });
    } catch (error) {
      console.error('Error in loadGoogleFont:', error);
    }
  };

  function validateHexColorCode(hexCode) {
    return /^#([0-9A-Fa-f]{3}){1,2}$/i.test(hexCode);
  }
  const h4 = '0.90 rem';
  const font = props.profileData?.global_font || 'IBM Plex Sans, sans-serif';

  // Listen for global font changes from anywhere in the app
  useEffect(() => {
    const onGlobalFontChange = () => {
      const newFont = props.profileData?.global_font || 'IBM Plex Sans, sans-serif';
      loadGoogleFont(newFont);
      applyGlobalFont(newFont);
    };
    window.addEventListener('globalfontchange', onGlobalFontChange);
    return () => window.removeEventListener('globalfontchange', onGlobalFontChange);
  }, []);

  const lightTh = useMemo(
    () =>
      createTheme({
        palette: {
          primary: {
            main: validateHexColorCode(primaryDark)
              ? primaryDark + '!important'
              : '#445AC7 !important',
            dark: validateHexColorCode(primaryDark) ? primaryDark : '#445AC7',
          },
          secondary: {
            main: '#FBFBFB',
            subClr: '#FBFBFB'
          },
          icon: {
            active: '#B4BDE9',
          },
          text: {
            primary: '#000000',
            grey: '#828282',
            helper: '#4F4F4F',
            yellow: '#F2F5A9',
          },
          table: {
            row: 'rgba(242, 242, 242, 0.3)',
            border: '#828282',
          },
          border: {
            main: '#E0E0E0',
            divider: '#7F8CC4',
          },
          button: {
            red: '#EB5757',
            green: '#27AE60',
          },
          tabs: {
            tabActive: '#858AA3',
            text: '#ECEFF8',
          },
          black,
          white,
          gradient: {
            appLauncher: 'linear-gradient(135deg, #32009A 0%, #abceff 100%)',
          },
          appLauncher: {
            tabStyle: '#32009A',
            tabStyleFont: 'white',
            tabBackground: 'linear-gradient(135deg, #819FF7 0%, #FFF 100%)',
            tabContainer: '#FFFFFF',
            paper: '#32009A',
          },
          notification: {
            grey: '#DADFF2',
          },
        },
        typography: {
          fontFamily: 'IBM Plex Sans, sans-serif',
          h1: {
            color: primaryDark,
            fontSize: '1.125em !important',// 18px main heading
            fontWeight: 500,
            textTransform: 'uppercase'
          },
          h2: {
            color: primaryDark,
            fontSize: '1em !important',//16px sub title1
            fontWeight: 500,
          },
          h3: {
            color: primaryDark,
            fontSize: '0.938em !important',//15px sub titile2
          },
          h4: {
            color: primaryDark,
            fontSize: '0.875em !important', //"14px" text
          },
          h5: {
            color: primaryDark,
            fontSize: '0.813 em !important',//13px sub text
          },
          h6: {
            fontSize: '0.75 em',//12px small
            color: primaryDark,
          },
          h7: {
            color: primaryDark,
            fontSize: '1em !important',
          },
          h8: {
            fontSize: 14,
          },
          subtitle1: {
            color: primaryDark,
            // fontSize: 36,
          },
          button: {
            textTransform: 'none',
          },
          caption: {
            color: 'black',
          },
        },
        mixins: {
          MaterialHeader: {
            whiteSpace: 'nowrap',
            // fontSize: h4 + '!important',
            fontSize: '0.875emrem',
            padding: '5px 2px',
            textAlign: 'center',
            color: 'white',
            backgroundColor: primaryDark,
            fontFamily: font,
            borderRight: '1px solid #EEEEEE',
          },
          MaterialCell: {
            fontFamily: font,
            fontSize: h4 + '!important',
            textAlign: 'center',
            borderRight: '1px solid #EEEEEE',
            color: 'black !important',
            padding: '5px 0px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: 150,
            width: 80,
            minWidth: 50,
          },
        },
        // overrides: {
        //   // Override styles for material-table components
        //   MuiTable: {
        //     root: {
        //       backgroundColor: primaryDark + "!important",
        //       color: "white",
        //       fontSize: "0.90em"
        //     },
        //   },
        //   MuiTableCell: {
        //     root: {
        //       border: "1px solid #EEEEEE",
        //       borderBottomWidth: "1px",
        //       padding: 7,
        //       fontSize: '0.90em'
        //     },
        //     head: {
        //       backgroundColor: primaryDark + "!important",
        //       color: "white",
        //       fontSize: "0.90em",
        //     },
        //   },
        //   MuiButton: {
        //     root: {
        //       padding: '6px 16px !important',
        //       '&:hover': {
        //         backgroundColor: primaryDark,
        //         color: white,
        //         //border: '1px solid ' + primaryDark,
        //         // change this to your desired hover color
        //       },
        //     },
        //     containedPrimary: {
        //       backgroundColor: primaryDark,
        //       padding: '6px 16px !important',
        //       color: white + "!important",
        //       '&:hover': {
        //         backgroundColor: primaryDark,
        //         //color: primaryDark,
        //         border: '1px solid ' + black,
        //         //backgroundColor: 'white',
        //         //color:primaryDark,
        //         outlineColor: 'transparent',
        //         outlineStyle: 'solid',
        //         boxShadow: '0 0 0 6px transparent',
        //         transition: '0.7s',
        //       },
        //     },
        //     outlinedPrimary: {
        //       color: primaryDark,
        //       border: '1px solid ' + primaryDark,
        //       padding: '6px 16px !important',
        //       '&:hover': {
        //         //color: primaryDark,
        //         border: '1px solid ' + primaryDark + "!important",
        //         outlineColor: 'transparent',
        //         outlineStyle: 'solid',
        //         boxShadow: '0 0 0 6px transparent',
        //         transition: '0.7s',
        //       },
        //     }
        //   },
        //   MuiFab: {
        //     root: {
        //       color: '#FFFFFF !important',
        //       border: '1px solid ' + primaryDark + ' !important'
        //     }
        //   }
        // },
        components: {
          MuiModal: {
            defaultProps: {
              disableScrollLock: true, // Prevents body from being locked
            },
          },
          MuiPopover: {
            defaultProps: {
              disableScrollLock: true, // Also applies to Menu, Popovers, and Tooltips
            },
          },
          MuiMenu: {
            defaultProps: {
              disableScrollLock: true, // Explicitly ensure Menu does not lock scrolling
            },
          },
          MuiTypography: {
            styleOverrides: {
              root: {
                fontSize: '1em',
                padding: '4px',
                // height: 'initial',
                //alignContent: 'center'
              },

            },
          },
          MuiTableCell: {
            styleOverrides: {
              root: {
                border: '1px solid #EEEEEE',
                borderBottomWidth: '1px',
                padding: 7,
                fontSize: '0.90em',
              },
              head: {
                backgroundColor: primaryDark + '!important',
                color: 'white !important',
                fontSize: '0.90em',
              },
            },
          },
          MuiTableHead: {
            styleOverrides: {
              root: {
                backgroundColor: primaryDark + '!important',
                color: 'white !important',
                fontSize: '0.90em !important',
              },
            },
          },
          MuiIconButton: {
            styleOverrides: {
              root: {
                padding: '2px !important',
              },
            },
          },
          MuiTable: {
            styleOverrides: {
              root: {
                marginTop: 16,
                borderBottom: '1px solid #EEEEEE',
                padding: 7,
              },
            },
          },
          MuiButton: {
            styleOverrides: {
              root: {
                padding: '6px 16px !important',
                '&:disabled': {
                  backgroundColor: '#D3D3D3 !important',
                  borderColor: '#D3D3D3 !important',
                  color: '#A9A9A9 !important',
                },
                '&:hover': {
                  backgroundColor: primaryDark,
                  color: white + '!important',
                },
              },
              containedPrimary: {
                backgroundColor: primaryDark,
                padding: '6px 16px !important',
                color: white + '!important',
                '&:hover': {
                  backgroundColor: primaryDark,
                  color: white + '!important',
                  border: '1px solid ' + black,
                  //backgroundColor: 'white',
                  //color:primaryDark,
                  outlineColor: 'transparent',
                  outlineStyle: 'solid',
                  boxShadow: '0 0 0 6px transparent',
                  transition: '0.7s',
                },
              },
              outlinedPrimary: {
                color: primaryDark,
                border: '1px solid ' + primaryDark,
                padding: '6px 16px !important',
                '&:hover': {
                  //color: primaryDark,
                  border: '1px solid ' + primaryDark + '!important',
                  outlineColor: 'transparent',
                  outlineStyle: 'solid',
                  boxShadow: '0 0 0 6px transparent',
                  transition: '0.7s',
                },
              },
            },
          },
          MuiAutocomplete: {
            styleOverrides: {
              root: {
                height: '25px',
                padding: '0px',
              },
              tag: {
                backgroundColor: '#E1E4F3',
                borderRadius: 20,
              },
            },
          },
          /* MuiFormLabel: {
             root: {
               color: primaryDark,
               fontSize:h3
             },
           },*/
          asterisk: {
            color: 'red',
            '&$error': {
              color: 'red',
            },
            fontSize: 13,
          },
          // MuiInput: {
          //   styleOverrides: {
          //     underline: {
          //       "&:before": {
          //         borderBottom: "0px solid",
          //         borderColor: "#E0E0E0",
          //       },
          //       "&:after": {
          //         borderBottom: "0px solid",
          //       },
          //       "&&&&:hover:before": {
          //         borderBottom: "0px solid",
          //         borderColor: "#E0E0E0",
          //       },
          //     },
          //   }
          // },
          MuiOutlinedInput: {
            styleOverrides: {
              root: {
                borderRadius: 0,
                border: '1px solid',
                borderColor: '#E0E0E0',
                '&$focused $notchedOutline': {
                  border: '0px solid grey !important',
                },
              },
              input: {
                padding: '4px 6px 6px',
                paddingLeft: '6px !important',
                border: 0,
                textOverflow: 'ellipsis',
                alignContent: 'center',
              },
              notchedOutline: {
                border: '0px solid',
              },
            },
          },
          MuiTab: {
            styleOverrides: {
              root: {
                '&$selected': {
                  backgroundColor: '#858AA3',
                  color: '#ECEFF8',
                  '&:hover': {
                    backgroundColor: '#858AA3',
                    color: '#ECEFF8',
                  },
                  borderTopLeftRadius: 7,
                  borderTopRightRadius: 7,
                },
              },
            },
          },
          MuiLink: {
            styleOverrides: {
              root: {
                fontSize: 14,
                borderBottom: '1px solid #445AC7',
                lineHeight: 1,
              },
            },
          },
          MuiFormHelperText: {
            styleOverrides: {
              root: {
                fontSize: 11,
                margin: 9,
              },
            },
          },
          MuiStepIcon: {
            styleOverrides: {
              root: {
                '& $text': {
                  fontSize: 12,
                },
                '&$active': {
                  border: '1px solid',
                  borderColor: primaryDark,
                  padding: 5,
                  margin: -5,
                  borderRadius: 26,
                  backgroundColor: '#FFF',
                  zIndex: 100,
                },
                width: 26,
                height: 26,
              },
            },
          },
          // MuiFormControl: {
          //   styleOverrides: {
          //     root: {
          //       width: "100% !important",
          //     },
          //     filterPanel: {
          //       '& .MuiFormControl-root': {
          //         width: '0px !important', // Adjust as needed
          //       },
          //       '& .MuiDataGrid-filterFormDeleteIcon': {
          //         width: '10px !important',
          //       },
          //     },
          //   }
          // },
          MuiFormControl: {
            styleOverrides: {
              root: {
                // width: "100% !important",
                width: '100% ',
              },
            },
          },
          MuiSelect: {
            defaultProps: {
              MenuProps: {
                PaperProps: {
                  style: {
                    maxHeight: 280,
                    overflowY: 'auto', // 'scroll' can be replaced with 'auto' for better UX
                    scrollBehavior: 'smooth', // Fixed typo
                    marginTop: 0, // Fixed capitalization
                  },
                },
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'left',
                },
                transformOrigin: {
                  vertical: 'top',
                  horizontal: 'left',
                },
              },
            },
            styleOverrides: {
              root: {
                marginTop: 0,
                display: 'flex !important',
                alignItems: 'flex-start',
                justifyContent: 'left',
                padding: '4px',
              },
              select: {
                padding: '2px 6px',
              },
              menu: {
                // display: 'block !important',
              },
              paper: {
                display: 'flex !important',
                flexDirection: 'column !important',
              },
            },
          },
          MuiMenuItem: {
            styleOverrides: {
              root: {
                //display: 'inline-flex !important',
                alignItems: 'center !important',
                display: 'flex !important',
                justifyContent: 'left !important',
                padding: '6px 16px !important',
              },
            },
          },
          MuiPopover: {
            styleOverrides: {
              paper: {
                boxShadow:
                  '0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12)',
                marginTop: 10,
              },
            },
          },
          MuiFormLabel: {
            styleOverrides: {
              root: {
                color: '#000000',
                fontSize: h3,
                "&.Mui-disabled": {
                  color: colors.text.disabled + " !important"
                }
              },
              asterisk: {
                color: colors.text.required + " !important",//'red',
                '&$error': {
                  color: 'red',
                },
              },
              disabled: {
                color: colors.disabled
              }
            },
          },
          // MuiOutlinedInput: {
          //   root: {
          //     borderRadius: 0,
          //     border: '1px solid',
          //     borderColor: '#E0E0E0',
          //     "&$focused $notchedOutline": {
          //         "border": "0px solid grey !important"
          //     }
          // },
          // input: {
          //     padding: '8px 0px 9px',
          //     border: 0,
          //     textOverflow: 'ellipsis'
          // },
          // notchedOutline: {
          //     border: '0px solid'
          // }
          // },
          MuiCardHeader: {
            styleOverrides: {
              root: {
                marginTop: 18,
                marginLeft: 12,
                fontSize: h3 + '!important',
              },
            },
          },
          MuiTabs: {
            styleOverrides: {
              indicator: {
                display: 'none',
              },
              fixed: {
                borderBottom: '2px solid',
                borderBottomColor: '#858AA3',
              },
              scrollable: {
                borderBottom: '2px solid',
                borderBottomColor: '#858AA3',
                marginBottom: '0px !important',
              },
            },
          },
          // MuiTabs: {
          //   underline: {
          //     "&:before": {
          //       borderBottom: "0px solid",
          //       borderColor: "#E0E0E0",
          //     },
          //     "&:after": {
          //       borderBottom: "0px solid",
          //     },
          //     "&&&&:hover:before": {
          //       borderBottom: "0px solid",
          //       borderColor: "#E0E0E0",
          //     },
          //   },
          // },
          // MuiChip: {
          //   styleOverrides: {
          //     deleteIcon: {
          //       height: 16,
          //       color: "#445AC7",
          //       margin: 0,
          //     },
          //   }
          // },
          MuiChipInput: {
            styleOverrides: {
              root: {
                width: '100% !important',
                padding: 5,
              },
              input: {
                width: '100%',
                position: 'relative !important',
                backgroundColor: 'white !important',
              },
            },
          },
          // MuiChip: {
          //   styleOverrides: {
          //     root: {
          //       border: "1px solid" + primaryDark,
          //       color: primaryDark
          //     }
          //   }
          // },
          MuiDataGrid: {
            styleOverrides: {
              root: {
                '& .MuiDataGrid-columnsContainer': {
                  backgroundColor: '#445AC7',
                  color: 'white',
                },
                '& .MuiDataGrid-footer': {
                  display: 'none',
                },
              },
            },
          },
          MuiFab: {
            styleOverrides: {
              root: {
                color: '#FFFFFF !important',
                border: '2px solid ' + primaryDark + ' !important',
                borderRadius: '20px !important',
                padding: '3px 4px!important',
                fontSize: '15px !important',
                justifyContent: 'space-between',
              },
            },
          },
        },
        shadows: Array(25).fill('none'), //removing shadows globally,
      }),
    [primaryDark],
  );
  //specifically for Promotions
  const promoTheme = useMemo(
    () =>
      createTheme({
        palette: {
          primary: {
            main: '#FFC400 !important',
            dark: '#FFC400',
          },
          secondary: {
            main: '#19170F',
            subClr: 'white'//'#FBFBFB'
          },
          icon: {
            active: '#B4BDE9',
          },
          text: {
            primary: '#000000',
            primaryDark: '#4E4E4E',
            grey: '#8D8D8D',
            helper: '#4F4F4F',
            yellow: '#FFC400',
            error: '#D32F2F',
          },
          table: {
            row: 'rgba(242, 242, 242, 0.3)',
            border: '#828282',
          },
          border: {
            main: '#E0E0E0',
            divider: '#7F8CC4',
          },
          button: {
            red: '#EB5757',
            green: '#27AE60',
          },
          tabs: {
            tabActive: '#858AA3',
            text: '#ECEFF8',
          },
          black,
          white,
          gradient: {
            appLauncher: 'linear-gradient(135deg, #32009A 0%, #abceff 100%)',
          },
          appLauncher: {
            tabStyle: '#32009A',
            tabStyleFont: 'white',
            tabBackground: 'linear-gradient(135deg, #819FF7 0%, #FFF 100%)',
            tabContainer: '#FFFFFF',
            paper: '#32009A',
          },
          notification: {
            grey: '#DADFF2',
          },
        },
        typography: {
          fontFamily: 'IBM Plex Sans, sans-serif',
          fontFeatureSettings: "'liga' off, 'clig' off !important",
          h1: {
            color: '#000',
            fontSize: '18px !important',
            fontWeight: 500,
          },
          h2: {
            color: '#000',
            fontSize: '16px !important',
            fontWeight: 500,
          },
          h3: {
            color: '#000',
            fontSize: '15px !important',
          },
          h4: {
            color: '#000',
            fontSize: '14px !important', //".75eim",
          },
          h5: {
            color: '#000',
            fontSize: '13px !important',
            fontWeight: 400,
            fontStyle: 'normal',
          },
          h6: {
            fontSize: '12px !important',
            color: '#000',
            fontWeight: 400,
            fontStyle: 'normal',
          },
          h7: {
            color: '#000',
            fontSize: '10px !important',
            fontWeight: 400,
            fontStyle: 'normal',
          },
          subtitle1: {
            color: primaryDark,
            // fontSize: 36,
          },
          button: {
            textTransform: 'none',
          },
          caption: {
            color: 'black',
          },
        },
        components: {
          MuiModal: {
            defaultProps: {
              disableScrollLock: true, // Prevents body from being locked
            },
          },
          MuiPopover: {
            defaultProps: {
              disableScrollLock: true, // Also applies to Menu, Popovers, and Tooltips
            },
          },
          MuiMenu: {
            defaultProps: {
              disableScrollLock: true, // Explicitly ensure Menu does not lock scrolling
            },
          },
          MuiSvgIcon: {
            styleOverrides: {
              root: {
                width: '20px',
                height: '20px',
              },
            },
          },
          MuiTypography: {
            styleOverrides: {
              root: {
                fontSize: '1em',
                padding: '4px',
                // height: 'initial',
                //alignContent: 'center'
              },
            },
          },
          MuiTableCell: {
            styleOverrides: {
              root: {
                fontFeatureSettings: "'liga' off, 'clig' off !important",
                borderRadius: '0px 4px 0px 0px',
                borderRight: '0.8px solid #E2E2E2',
                borderLeft: ' 0.8px solid  #E2E2E2',
                padding: '1 rem',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                color: '#19170F !important',
                fontWeight: '400',
                fontStyle: 'normal',
                lineHeight: '1 rem' /* 114.286% */,
                letterSpacing: '1.12px',
                fontSize: '0.875rem',
              },
              head: {
                background: '#F8F8F8 !important',
                color: '#8D8D8D!important',
                fontSize: '0.75rem',
              },
            },
          },
          MuiAccordion: {
            styleOverrides: {
              root: {
                '&::before': {
                  height: 0, // Removes the dividing line
                },
              },
            },
          },
          MuiCheckbox: {
            styleOverrides: {
              root: {
                color: '#A7A7A7', // Default color
              },
            },
          },
          MuiIconButton: {
            styleOverrides: {
              root: {
                padding: '2px !important',
              },
            },
          },
          MuiTable: {
            styleOverrides: {
              root: {
                marginTop: 16,
                borderBottom: '1px solid #EEEEEE',
                padding: 7,
              },
            },
          },
          MuiButton: {
            styleOverrides: {
              root: {
                display: 'flex',
                padding: '5px 16px',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '8px',
                borderRadius: '4px',
                //minHeight: '30px',
                height: '30px',
                '&:disabled': {
                  border: '1px solid  #E2E2E2',
                  //background: ' #FFC400',
                  boxShadow: '0px 0px 0px 1px rgba(243, 194, 32, 0.25)',
                },
              },
              containedPrimary: {
                backgroundColor: primaryDark,
                padding: '6px 16px !important',
                color: '#000 !important',
                '&:hover': {
                  border: '1px solid #EFA610',
                  background: ' #FFC400',
                  boxShadow: '0px 0px 0px 1px rgba(243, 194, 32, 0.25)',
                  transition: '0.7s',
                },
                '&:disabled': {
                  background: primaryDark,
                  opacity: 0.5,
                },
              },
              outlinedPrimary: {
                color: '#7F7F7F',
                border: '1px solid  #E2E2E2',
                background: '#FFF',
                boxShadow: '0px 0px 0px 1px rgba(239, 239, 239, 0.25)',
                '&:hover': {
                  color: '#4E4E4E',
                  boxShadow:
                    ' 0px 0px 0px 2px #F1F1F1, 0px 0px 0px 3px #FFC400',
                },
                '&:disabled': {
                  opacity: 0.7,
                  boxShadow: '0px 0px 0px 1px rgba(239, 239, 239, 0.25)',
                },
              },
            },
          },
          MuiAutocomplete: {
            styleOverrides: {
              root: {
                height: '25px',
                padding: '0px',
              },
              tag: {
                backgroundColor: '#E1E4F3',
                borderRadius: 20,
              },
            },
          },
          asterisk: {
            color: 'red',
            '&$error': {
              color: 'red',
            },
            fontSize: 13,
          },
          MuiOutlinedInput: {
            styleOverrides: {
              root: {
                borderRadius: 4,
                border: '1px solid',
                borderColor: '#E0E0E0',
                // "&$focused $notchedOutline": {
                //   border: "0px solid grey !important"
                // },
              },
              input: {
                padding: 8, //"4px 6px 6px",
                // paddingLeft: "6px !important",
                border: 0,
                textOverflow: 'ellipsis',
                alignContent: 'center',
                '&::placeholder': {
                  color: '#19170F',
                  fontSize: 14,
                  opacity: 1,
                  letterSpacing: '-0.084px',
                },
              },
              notchedOutline: {
                border: '0px solid',
              },
            },
          },
          MuiTab: {
            styleOverrides: {
              root: {
                '&$selected': {
                  backgroundColor: '#858AA3',
                  color: '#ECEFF8',
                  '&:hover': {
                    backgroundColor: '#858AA3',
                    color: '#ECEFF8',
                  },
                  borderTopLeftRadius: 7,
                  borderTopRightRadius: 7,
                },
              },
            },
          },
          MuiLink: {
            styleOverrides: {
              root: {
                fontSize: 14,
                borderBottom: '1px solid #445AC7',
                lineHeight: 1,
              },
            },
          },
          MuiFormHelperText: {
            styleOverrides: {
              root: {
                fontSize: 11,
                margin: 9,
              },
            },
          },
          MuiStepIcon: {
            styleOverrides: {
              root: {
                '& $text': {
                  fontSize: 12,
                },
                '&$active': {
                  border: '1px solid',
                  borderColor: primaryDark,
                  padding: 5,
                  margin: -5,
                  borderRadius: 26,
                  backgroundColor: '#FFF',
                  zIndex: 100,
                },
                width: 26,
                height: 26,
              },
            },
          },
          MuiFormControl: {
            styleOverrides: {
              root: {
                // width: "100% !important",
                width: '100% ',
              },
            },
          },
          MuiSelect: {
            styleOverrides: {
              root: {
                marginTop: 0,
                display: 'flex !important',
                alignItems: 'flex-start',
                justifyContent: 'left',
                padding: '4px',
              },
              select: {
                padding: '2px 6px',
              },
              paper: {
                display: 'flex !important',
                flexDirection: 'column !important',
              },
            },
            defaultProps: {
              // Set default icons globally
              IconComponent: (props) =>
                props.open ? (
                  <ExpandLess {...props} />
                ) : (
                  <ExpandMore {...props} />
                ),
            },
          },
          MuiMenuItem: {
            styleOverrides: {
              root: {
                //display: 'inline-flex !important',
                alignItems: 'center !important',
                display: 'flex !important',
                justifyContent: 'left !important',
                padding: '6px 16px !important',
              },
            },
          },
          MuiPopover: {
            styleOverrides: {
              paper: {
                boxShadow:
                  '0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12)',
                marginTop: 10,
              },
            },
          },
          MuiFormLabel: {
            styleOverrides: {
              root: {
                color: '#000000',
                fontSize: h3,
              },
              asterisk: {
                color: 'red',
                '&$error': {
                  color: 'red',
                },
              },
            },
          },
          MuiCardHeader: {
            styleOverrides: {
              root: {
                marginTop: 18,
                marginLeft: 12,
                fontSize: h3 + '!important',
              },
            },
          },
          MuiTabs: {
            styleOverrides: {
              indicator: {
                display: 'none',
              },
              fixed: {
                borderBottom: '2px solid',
                borderBottomColor: '#858AA3',
              },
              scrollable: {
                borderBottom: '2px solid',
                borderBottomColor: '#858AA3',
                marginBottom: '0px !important',
              },
            },
          },
          MuiChipInput: {
            styleOverrides: {
              root: {
                width: '100% !important',
                padding: 5,
              },
              input: {
                width: '100%',
                position: 'relative !important',
                backgroundColor: 'white !important',
              },
            },
          },
          MuiDataGrid: {
            styleOverrides: {
              root: {
                '& .MuiDataGrid-columnsContainer': {
                  backgroundColor: '#445AC7',
                  color: 'white',
                },
                '& .MuiDataGrid-footer': {
                  display: 'none',
                },
              },
            },
          },
          MuiFab: {
            styleOverrides: {
              root: {
                color: '#FFFFFF !important',
                border: '2px solid ' + primaryDark + ' !important',
                borderRadius: '20px !important',
                padding: '3px 4px!important',
                fontSize: '15px !important',
                justifyContent: 'space-between',
              },
            },
          },
        },
        shadows: Array(25).fill('none'), //removing shadows globally,
      }),
    [primaryDark],
  );
  useEffect(() => {
    const initializeFont = () => {
      try {
        // Get saved font from the profile or use default
        const savedFont = props.profileData?.global_font || 'IBM Plex Sans, sans-serif';

        // Load the font
        loadGoogleFont(savedFont);

        // Apply the font globally
        applyGlobalFont(savedFont);
      } catch (error) {
        console.error('Error initializing font:', error);
      }
    };

    initializeFont();
  }, []);
  const handleFontChange = (newFont) => {
    if (!newFont) return;

    // Load the new font
    loadGoogleFont(newFont);

    // Apply globally
    applyGlobalFont(newFont);

    // Update in redux if needed
    // You might want to dispatch an action here to update the font in your global state
  };

  useEffect(() => {
    return history.listen((location) => {
      persistor.purge();
    });
  }, [history]);
  useEffect(() => {
    const handleTabOpen = () => {
      const tabsCount =
        parseInt(localStorage.getItem('tabsCount') || '0', 10) + 1;
      localStorage.setItem('tabsCount', tabsCount.toString());
      if (localStorage.getItem('loginData')) {
        sessionStorage.setItem('loginData', localStorage.getItem('loginData'));
        localStorage.removeItem('loginData');
      }
      if (localStorage.getItem('application')) {
        sessionStorage.setItem(
          'application',
          localStorage.getItem('application'),
        );
        localStorage.removeItem('application');
      }
    };
    const handleTabClose = () => {
      let tabsCount =
        parseInt(localStorage.getItem('tabsCount') || '1', 10) - 1;
      if (tabsCount < 0) {
        tabsCount = 0;
      }
      localStorage.setItem('tabsCount', tabsCount.toString());
      if (tabsCount === 0) {
        // Clear all storage data when the last tab is closed
        localStorage.clear();
        // Redirect to the login page
        window.location.href = '/login';
      }
    };
    window.addEventListener('DOMContentLoaded', handleTabOpen);
    window.addEventListener('beforeunload', handleTabClose);
    return () => {
      window.removeEventListener('DOMContentLoaded', handleTabOpen);
      window.removeEventListener('beforeunload', handleTabClose);
    };
  }, []);
  useEffect(() => {
    if (document.referrer === '') {
    }
  });
  useEffect(() => {
    setTabCount(localStorage.getItem('tabsOpen'));
  }, [localStorage.getItem('tabsOpen')]);
  // useEffect(() => {
  //   console.log(domHistory);
  //   console.log(props.lockDetails);
  // }, [domHistory]);

  if (isProfileLoading) {
    return <CircularProgress style={{ margin: 'auto', display: 'block' }} />; // Show loader while profile is loading
  }

  return (
    <div>
      <style>
        {`
      .e-grid .e-headercell {
        background-color:` +
          primaryDark +
          ` !important; /* Replace with your desired color */
      }
      
      .MuiFormLabel-root.Mui-focused {
        background-color: white !important;
      }
      `}
      </style>

      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={promoActive ? promoTheme : lightTh}>
          <Provider store={store}>
            <Router>
              <PersistGate persistor={persistor}>
                <Suspense fallback={<div>Loading...</div>}>
                  <IdleTimerContainer />
                </Suspense>
                <ToastContainer
                  position="top-right"
                  autoClose={5000}
                  hideProgressBar={false}
                  newestOnTop={true}
                  closeOnClick
                  rtl={false}
                  pauseOnFocusLoss
                  draggable
                  pauseOnHover
                  theme="light"
                  style={{ zIndex: 9999 }}
                />
                <Suspense fallback={<div>Loading...</div>}>
                  <ToastBridge>
                    <RoutesApp />
                  </ToastBridge>
                </Suspense>
              </PersistGate>
            </Router>
          </Provider>
        </ThemeProvider>
      </StyledEngineProvider>
    </div>
  );
};
const mapStateToProps = (state) => {
  return {
    profileData: state.profileData.profileData,
    lockDetails: state.LockData.lockedData,
  };
};
const mapDispatchToProps = (dispatch) => {
  return {
    onRemoveLock: (data) => dispatch(removeLock(data)),
    getProfile: () => dispatch(getProfile()),
  };
};
export default connect(mapStateToProps, mapDispatchToProps)(App);